/**
 * Tools Marketplace Page
 * Main tools listing page with category navigation, search, and filtering
 *
 * Filter state is derived from URL search params (single source of truth).
 * This ensures browser history navigation and external URL changes work correctly.
 */

import { useMemo, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { ToolCard, ToolCategories, ToolFilters } from "@components/tools";
import { useLibraryResources } from "@hooks/useLibrary";
import type {
  LibraryDifficulty,
  LibraryResourceWithUserData,
} from "@/types/models";
import type { PricingTier, IntegrationType } from "@/types/tools";

// Valid filter values for type checking URL params
const VALID_DIFFICULTIES: LibraryDifficulty[] = [
  "beginner",
  "intermediate",
  "advanced",
];

const VALID_PRICING_TIERS: PricingTier[] = [
  "free",
  "freemium",
  "paid",
  "enterprise",
];

const VALID_INTEGRATION_TYPES: IntegrationType[] = [
  "api",
  "sdk",
  "cli",
  "standalone",
  "browser_extension",
  "saas",
];

export function ToolsListPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  // Derive filter state from URL params (URL is the source of truth)
  const selectedCategory = useMemo((): string | "all" => {
    const category = searchParams.get("category");
    return category || "all";
  }, [searchParams]);

  const selectedDifficulty = useMemo((): LibraryDifficulty | "all" => {
    const difficulty = searchParams.get("difficulty");
    return difficulty &&
      VALID_DIFFICULTIES.includes(difficulty as LibraryDifficulty)
      ? (difficulty as LibraryDifficulty)
      : "all";
  }, [searchParams]);

  const selectedPricingTier = useMemo((): PricingTier | "all" => {
    const tier = searchParams.get("pricing");
    return tier && VALID_PRICING_TIERS.includes(tier as PricingTier)
      ? (tier as PricingTier)
      : "all";
  }, [searchParams]);

  const selectedIntegrationType = useMemo((): IntegrationType | "all" => {
    const type = searchParams.get("integration");
    return type && VALID_INTEGRATION_TYPES.includes(type as IntegrationType)
      ? (type as IntegrationType)
      : "all";
  }, [searchParams]);

  const searchQuery = searchParams.get("search") || "";
  const showBookmarkedOnly = searchParams.get("bookmarked") === "true";

  // Track if the last URL update was from search input
  const lastActionWasSearchRef = useRef(false);

  // Helper to update URL params
  const updateParams = useCallback(
    (
      updates: Record<string, string | null>,
      options: { isSearchChange?: boolean } = {},
    ) => {
      const { isSearchChange = false } = options;
      const newParams = new URLSearchParams(searchParams);

      Object.entries(updates).forEach(([key, value]) => {
        if (value === null || value === "" || value === "all") {
          newParams.delete(key);
        } else {
          newParams.set(key, value);
        }
      });

      const shouldReplace = isSearchChange && lastActionWasSearchRef.current;
      setSearchParams(newParams, { replace: shouldReplace });
      lastActionWasSearchRef.current = isSearchChange;
    },
    [searchParams, setSearchParams],
  );

  // Filter setters that update URL params
  const setSelectedCategory = useCallback(
    (category: string | "all") => {
      updateParams({ category: category === "all" ? null : category });
    },
    [updateParams],
  );

  const setSelectedDifficulty = useCallback(
    (difficulty: LibraryDifficulty | "all") => {
      updateParams({ difficulty: difficulty === "all" ? null : difficulty });
    },
    [updateParams],
  );

  const setSearchQuery = useCallback(
    (search: string) => {
      updateParams({ search: search || null }, { isSearchChange: true });
    },
    [updateParams],
  );

  const setShowBookmarkedOnly = useCallback(
    (bookmarked: boolean) => {
      updateParams({ bookmarked: bookmarked ? "true" : null });
    },
    [updateParams],
  );

  const setPricingTier = useCallback(
    (tier: PricingTier | "all") => {
      updateParams({ pricing: tier === "all" ? null : tier });
    },
    [updateParams],
  );

  const setIntegrationType = useCallback(
    (type: IntegrationType | "all") => {
      updateParams({ integration: type === "all" ? null : type });
    },
    [updateParams],
  );

  // Fetch tools from API (filter by type=tool and optionally by topic/category)
  const {
    data: resources = [],
    isLoading,
    error,
  } = useLibraryResources({
    type: "tool", // Always filter to tools only
    topic: selectedCategory !== "all" ? selectedCategory : undefined,
    difficulty: selectedDifficulty !== "all" ? selectedDifficulty : undefined,
    search: searchQuery || undefined,
  });

  // Client-side filter for bookmarks
  const filteredTools = useMemo(() => {
    if (!showBookmarkedOnly) {
      return resources;
    }
    return resources.filter(
      (resource: LibraryResourceWithUserData) => resource.isBookmarked,
    );
  }, [resources, showBookmarkedOnly]);

  // Check if any filters are active
  const hasActiveFilters =
    selectedCategory !== "all" ||
    selectedDifficulty !== "all" ||
    selectedPricingTier !== "all" ||
    selectedIntegrationType !== "all" ||
    searchQuery !== "" ||
    showBookmarkedOnly;

  // Featured tools (only when no filters active)
  const featuredTools = useMemo(() => {
    if (hasActiveFilters) {
      return [];
    }
    return resources.filter(
      (resource: LibraryResourceWithUserData) => resource.isFeatured,
    );
  }, [resources, hasActiveFilters]);

  // Clear all filters by resetting URL params
  const handleClearFilters = useCallback(() => {
    setSearchParams(new URLSearchParams(), { replace: true });
  }, [setSearchParams]);

  // Loading state
  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <div className="h-9 w-48 bg-gray-200 dark:bg-gray-700 rounded animate-pulse mb-2" />
          <div className="h-6 w-80 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
        </div>
        {/* Category skeleton */}
        <div className="flex gap-2 mb-6">
          {[...Array(6)].map((_, i) => (
            <div
              key={i}
              className="h-10 w-24 bg-gray-200 dark:bg-gray-700 rounded-full animate-pulse"
            />
          ))}
        </div>
        {/* Grid skeleton */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(6)].map((_, i) => (
            <div
              key={i}
              className="h-64 bg-gray-200 dark:bg-gray-700 rounded-xl animate-pulse"
            />
          ))}
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center py-12 bg-red-50 dark:bg-red-900/20 rounded-xl border border-red-200 dark:border-red-800">
          <svg
            className="w-16 h-16 mx-auto text-red-400 dark:text-red-500 mb-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
          <h3 className="text-lg font-medium text-red-900 dark:text-red-100 mb-2">
            Failed to load tools
          </h3>
          <p className="text-red-700 dark:text-red-300 mb-4">
            {error instanceof Error
              ? error.message
              : "An unexpected error occurred"}
          </p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Page Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl flex items-center justify-center shadow-lg shadow-primary-500/25">
            <svg
              className="w-5 h-5 text-white"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M11.42 15.17L17.25 21A2.652 2.652 0 0021 17.25l-5.877-5.877M11.42 15.17l2.496-3.03c.317-.384.74-.626 1.208-.766M11.42 15.17l-4.655 5.653a2.548 2.548 0 11-3.586-3.586l6.837-5.63m5.108-.233c.55-.164 1.163-.188 1.743-.14a4.5 4.5 0 004.486-6.336l-3.276 3.277a3.004 3.004 0 01-2.25-2.25l3.276-3.276a4.5 4.5 0 00-6.336 4.486c.091 1.076-.071 2.264-.904 2.95l-.102.085m-1.745 1.437L5.909 7.5H4.5L2.25 3.75l1.5-1.5L7.5 4.5v1.409l4.26 4.26m-1.745 1.437l1.745-1.437m6.615 8.206L15.75 15.75M4.867 19.125h.008v.008h-.008v-.008z"
              />
            </svg>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            Tools Marketplace
          </h1>
        </div>
        <p className="text-lg text-gray-600 dark:text-gray-400">
          Discover AI development tools to enhance your workflow
        </p>
      </div>

      {/* Category Navigation */}
      <div className="mb-6">
        <ToolCategories
          selectedCategory={selectedCategory}
          onCategoryChange={setSelectedCategory}
        />
      </div>

      {/* Search and Filters */}
      <div className="mb-8">
        <ToolFilters
          searchQuery={searchQuery}
          selectedDifficulty={selectedDifficulty}
          selectedPricingTier={selectedPricingTier}
          selectedIntegrationType={selectedIntegrationType}
          showBookmarkedOnly={showBookmarkedOnly}
          onSearchChange={setSearchQuery}
          onDifficultyChange={setSelectedDifficulty}
          onPricingTierChange={setPricingTier}
          onIntegrationTypeChange={setIntegrationType}
          onBookmarkedChange={setShowBookmarkedOnly}
          onClearFilters={handleClearFilters}
          hasActiveFilters={hasActiveFilters}
          showMarketplaceFilters
        />
      </div>

      {/* Featured Section - only show when no filters active */}
      {!hasActiveFilters && featuredTools.length > 0 && (
        <div className="mb-10">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <svg
              className="w-5 h-5 text-amber-500"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
            </svg>
            Featured Tools
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {featuredTools.map((tool: LibraryResourceWithUserData) => (
              <ToolCard key={tool.id} tool={tool} showUserData />
            ))}
          </div>
        </div>
      )}

      {/* All Tools */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            {hasActiveFilters ? "Filtered Results" : "All Tools"}
          </h2>
          <span className="text-sm text-gray-500 dark:text-gray-400">
            {filteredTools.length} tool{filteredTools.length !== 1 ? "s" : ""}
          </span>
        </div>

        {filteredTools.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredTools.map((tool: LibraryResourceWithUserData) => (
              <ToolCard key={tool.id} tool={tool} showUserData />
            ))}
          </div>
        ) : (
          <div className="text-center py-12 bg-gray-50 dark:bg-gray-800/50 rounded-xl border border-gray-200 dark:border-gray-700">
            <svg
              className="w-16 h-16 mx-auto text-gray-400 dark:text-gray-600 mb-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M11.42 15.17L17.25 21A2.652 2.652 0 0021 17.25l-5.877-5.877M11.42 15.17l2.496-3.03c.317-.384.74-.626 1.208-.766M11.42 15.17l-4.655 5.653a2.548 2.548 0 11-3.586-3.586l6.837-5.63m5.108-.233c.55-.164 1.163-.188 1.743-.14a4.5 4.5 0 004.486-6.336l-3.276 3.277a3.004 3.004 0 01-2.25-2.25l3.276-3.276a4.5 4.5 0 00-6.336 4.486c.091 1.076-.071 2.264-.904 2.95l-.102.085m-1.745 1.437L5.909 7.5H4.5L2.25 3.75l1.5-1.5L7.5 4.5v1.409l4.26 4.26m-1.745 1.437l1.745-1.437m6.615 8.206L15.75 15.75M4.867 19.125h.008v.008h-.008v-.008z"
              />
            </svg>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              No tools found
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              {showBookmarkedOnly
                ? "You haven't saved any tools yet."
                : "Try adjusting your filters to find what you're looking for."}
            </p>
            <button
              onClick={handleClearFilters}
              className="px-4 py-2 text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors"
            >
              Clear all filters
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
